import type { MutableRefObject, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { LayoutChangeEvent, ScrollView } from 'react-native';

const DEFAULT_MIN_BOTTOM_PADDING = 100;
const ANCHOR_TOP_OFFSET_PX = 16;
const ANCHOR_SCROLL_DELAY_MS = 50;
const ANCHOR_ARRIVAL_EPSILON_PX = 2;
const ANCHOR_ARRIVAL_FALLBACK_MS = 600;
const FOOTER_CLEARANCE_PX = 16;
const ANCHOR_CORRECTION_WINDOW_MS = 400;

export interface UseTurnAnchoredScrollOptions {
    scrollViewRef: RefObject<ScrollView | null>;
    keyboardOffset?: number;
    footerHeight?: number;
    minPadding?: number;
}

export interface UseTurnAnchoredScrollResult {
    bottomPadding: number;
    scrollViewHeight: number;
    onScrollViewLayout: (height: number) => void;
    /** Feed in the real (unpadded) message content's measured height as it changes. */
    notifyContentHeight: (height: number) => void;
    naturalContentHeightRef: MutableRefObject<number>;
    /** Pin this message near the top of the viewport. `onAnchored` fires once the scroll arrives. */
    createAnchorOnLayout: (
        messageId: string,
        onAnchored?: () => void,
    ) => (event: LayoutChangeEvent) => void;
    /** Feed live scroll offsets in so a pending anchor can detect arrival. */
    notifyScrollOffset: (offsetY: number) => void;
    /** Scroll back to the last anchored user message position. */
    scrollToAnchor: (animated?: boolean) => void;
}

/**
 * Anchors the latest user turn near the top of the viewport and grows the
 * assistant reply into dynamic bottom padding so streaming never yanks the
 * viewport around. `bottomPadding` is kept at
 * `max(footerFloor, viewportHeight - contentBelowAnchor)`, recomputed off a
 * directly measured content height — this keeps the scrollable range below
 * the anchor from ever shrinking under the current scroll position, so
 * nothing gets clamped back into view.
 */
export const useTurnAnchoredScroll = ({
    scrollViewRef,
    keyboardOffset = 0,
    footerHeight = 0,
    minPadding = DEFAULT_MIN_BOTTOM_PADDING,
}: UseTurnAnchoredScrollOptions): UseTurnAnchoredScrollResult => {
    const [scrollViewHeight, setScrollViewHeight] = useState(0);
    const [bottomPadding, setBottomPadding] = useState(minPadding);
    const lastAnchorYRef = useRef(0);
    const naturalContentHeightRef = useRef(0);
    const scrolledForMessageRef = useRef<string | null>(null);
    const anchorFirstLayoutAtRef = useRef(0);
    const pendingAnchorRef = useRef<{
        targetY: number;
        onAnchored?: () => void;
        fallbackTimer: ReturnType<typeof setTimeout>;
    } | null>(null);

    const computeNeededPadding = useCallback(
        (naturalHeight: number) => {
            const contentBelowAnchor = Math.max(
                0,
                naturalHeight - lastAnchorYRef.current,
            );
            const effectiveViewportHeight = Math.max(
                0,
                scrollViewHeight - keyboardOffset,
            );
            const floor = Math.max(
                minPadding,
                footerHeight + FOOTER_CLEARANCE_PX + keyboardOffset,
            );
            return Math.max(
                floor,
                effectiveViewportHeight - contentBelowAnchor,
            );
        },
        [footerHeight, keyboardOffset, minPadding, scrollViewHeight],
    );

    const notifyContentHeight = useCallback(
        (height: number) => {
            naturalContentHeightRef.current = height;
            setBottomPadding(computeNeededPadding(height));
        },
        [computeNeededPadding],
    );

    // Recompute on viewport/keyboard/footer changes, not just content height.
    useEffect(() => {
        setBottomPadding(computeNeededPadding(naturalContentHeightRef.current));
    }, [computeNeededPadding]);

    const resolvePendingAnchor = useCallback(() => {
        const pending = pendingAnchorRef.current;
        if (pending == null) return;
        clearTimeout(pending.fallbackTimer);
        pendingAnchorRef.current = null;
        pending.onAnchored?.();
    }, []);

    const notifyScrollOffset = useCallback(
        (offsetY: number) => {
            const pending = pendingAnchorRef.current;
            if (pending == null) return;
            if (
                Math.abs(offsetY - pending.targetY) <= ANCHOR_ARRIVAL_EPSILON_PX
            ) {
                resolvePendingAnchor();
            }
        },
        [resolvePendingAnchor],
    );

    const createAnchorOnLayout = useCallback(
        (messageId: string, onAnchored?: () => void) =>
            (event: LayoutChangeEvent) => {
                const isNewMessage =
                    scrolledForMessageRef.current !== messageId;
                if (isNewMessage) {
                    scrolledForMessageRef.current = messageId;
                    anchorFirstLayoutAtRef.current = Date.now();
                } else if (
                    Date.now() - anchorFirstLayoutAtRef.current >
                    ANCHOR_CORRECTION_WINDOW_MS
                ) {
                    return;
                }

                const targetY = event.nativeEvent.layout.y;
                lastAnchorYRef.current = targetY;
                setBottomPadding(
                    computeNeededPadding(naturalContentHeightRef.current),
                );
                setTimeout(() => {
                    const clampedTargetY = Math.max(
                        ANCHOR_TOP_OFFSET_PX,
                        targetY - ANCHOR_TOP_OFFSET_PX,
                    );
                    scrollViewRef.current?.scrollTo({
                        y: clampedTargetY,
                        animated: true,
                    });
                    if (pendingAnchorRef.current != null) {
                        clearTimeout(pendingAnchorRef.current.fallbackTimer);
                    }
                    pendingAnchorRef.current = {
                        targetY: clampedTargetY,
                        onAnchored,
                        fallbackTimer: setTimeout(
                            resolvePendingAnchor,
                            ANCHOR_ARRIVAL_FALLBACK_MS,
                        ),
                    };
                }, ANCHOR_SCROLL_DELAY_MS);
            },
        [computeNeededPadding, scrollViewRef, resolvePendingAnchor],
    );

    useEffect(() => {
        return () => {
            if (pendingAnchorRef.current != null) {
                clearTimeout(pendingAnchorRef.current.fallbackTimer);
            }
        };
    }, []);

    const scrollToAnchor = useCallback(
        (animated = true) => {
            const targetY = Math.max(
                ANCHOR_TOP_OFFSET_PX,
                lastAnchorYRef.current - ANCHOR_TOP_OFFSET_PX,
            );
            scrollViewRef.current?.scrollTo({ y: targetY, animated });
        },
        [scrollViewRef],
    );

    return {
        bottomPadding,
        scrollViewHeight,
        onScrollViewLayout: setScrollViewHeight,
        notifyContentHeight,
        naturalContentHeightRef,
        createAnchorOnLayout,
        notifyScrollOffset,
        scrollToAnchor,
    };
};
